import type { Attachment } from '@octopus-do/octopus-project-core'
import cx from 'classnames'
import type React from 'react'
import Draggable from 'react-draggable'

import { useScaleZoom } from '@/hooks'
import css from './DraggableNode.module.scss'
import {
  useClearSelect,
  useCreateArrow,
  useDraggablePosition,
  useSelect,
} from './hooks'

interface Props {
  type: string
  attachment: Attachment
  disabled?: boolean
  selected: boolean
  children?: React.ReactNode
}

export const DraggableNode: React.FC<Props> = ({
  attachment: { offset, id, type },
  selected,
  disabled = false,
  children,
}) => {
  const onPointerDown = useCreateArrow(id)

  const { scale } = useScaleZoom()
  const { isDragging, onDrag, onStop } = useDraggablePosition(id, offset)

  const { ref } = useClearSelect(selected, id)

  const onClick = useSelect(isDragging, id)

  const classes = cx(css.container, {
    [css.dragging]: isDragging,
    [css.disabled]: disabled,
  })

  return (
    <Draggable
      scale={scale}
      disabled={disabled}
      position={offset}
      onDrag={onDrag}
      onStop={onStop}
      grid={[3, 3]}
      nodeRef={ref}
    >
      <div
        onPointerDown={onPointerDown}
        ref={ref}
        onPointerUp={onClick}
        className={classes}
        data-node-type={type}
        data-click-target={id}
        key={id}
        id={id}
        draggable={false}
      >
        {children}
      </div>
    </Draggable>
  )
}
